import React, { useMemo } from 'react';
import DataTable from '../DataTable';
import StatusChip from '../common/StatusChip';

/**
 * Tabla de alertas abiertas.
 *
 * Se apoya en el DataTable genérico (@tanstack + DataTableShell): así hereda
 * el `min-width` con scroll, el pie de paginación, el esqueleto de carga y la
 * tarjeta por registro en móvil, en vez de repetir 800 líneas de <Table> con
 * estilos en línea.
 *
 * `min-width: 900px` es el mínimo medido para las seis columnas.
 */
const MIN_WIDTH = 900;

// El gateway manda las etiquetas ya traducidas; aquí sólo se les asigna tono.
const LEVEL_TONE = {
  Critica: 'danger',
  Advertencia: 'warning',
  Informativa: 'info',
};

const LEVEL_ICON = {
  Critica: 'ri-error-warning-line',
  Advertencia: 'ri-alert-line',
  Informativa: 'ri-information-line',
};

const STATUS_TONE = {
  nueva: 'warning',
  'en seguimiento': 'info',
  resuelta: 'success',
};

const AlertTable = ({
  alerts,
  pagination,
  isLoading,
  filterLevel,
  filterStatus,
  searchTerm,
  onFilterLevelChange,
  onFilterStatusChange,
  onSearchChange,
  onSearchEnter,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  updateStatus,
  isUpdatingStatus,
  onViewAlert,
}) => {

  const columns = useMemo(
    () => [
      {
        header: 'Detector',
        accessorKey: 'title',
        cell: (info) => (
          <span style={{ fontWeight: 600, fontSize: 13 }}>{info.getValue()}</span>
        ),
      },
      {
        header: 'Sujeto',
        accessorKey: 'user',
        cell: (info) => {
          const alert = info.row.original;
          return (
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                {alert.user || '—'}
              </div>
              {alert.code && <div className="ui-mono">{alert.code}</div>}
            </div>
          );
        },
      },
      {
        header: 'Severidad',
        accessorKey: 'level',
        cell: (info) => {
          const level = info.getValue();
          return (
            <StatusChip
              tone={LEVEL_TONE[level] || 'neutral'}
              icon={LEVEL_ICON[level]}
            >
              {level}
            </StatusChip>
          );
        },
      },
      {
        header: 'Detectada',
        accessorKey: 'date',
        cell: (info) => {
          const alert = info.row.original;
          return (
            <div style={{ whiteSpace: 'nowrap' }}>
              <div style={{ fontSize: 12.5, color: '#5A6187' }}>{alert.date}</div>
              <div style={{ fontSize: 11, color: '#8A91AE' }}>{alert.time}</div>
            </div>
          );
        },
      },
      {
        header: 'Estado',
        accessorKey: 'status',
        cell: (info) => {
          const status = info.getValue();
          return (
            <StatusChip
              tone={STATUS_TONE[String(status || '').trim().toLowerCase()] || 'neutral'}
            >
              {status}
            </StatusChip>
          );
        },
      },
      {
        header: 'Acciones',
        accessorKey: 'actions',
        cell: (info) => {
          const alert = info.row.original;
          return (
            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="ui-btn ui-btn--ghost ui-btn--sm"
                onClick={() => onViewAlert(alert)}
              >
                Ver detalle
              </button>

              {/*
                Las tres transiciones de estado eran un menú "⋯" con tres
                entradas; con una sola acción frecuente —dar por resuelta— el
                menú sobraba. Reabrir y "en seguimiento" siguen disponibles
                desde el detalle.
              */}
              <button
                type="button"
                className="ui-icon-btn"
                title="Marcar como resuelta"
                disabled={isUpdatingStatus || alert.status === 'Resuelta'}
                onClick={() => updateStatus(alert.id, 'Resuelta')}
              >
                <i className="ri-check-line" />
              </button>
            </div>
          );
        },
      },
    ],
    [onViewAlert, updateStatus, isUpdatingStatus]
  );

  const filters = (
    <>
      <h3 className="ui-card__title" style={{ marginRight: 'auto' }}>
        Alertas abiertas
      </h3>

      <span className="ui-filter-select">
        <select value={filterLevel} onChange={onFilterLevelChange} aria-label="Severidad">
          <option>Todos los Niveles</option>
          <option>Critica</option>
          <option>Advertencia</option>
          <option>Informativa</option>
        </select>
      </span>

      <span className="ui-filter-select">
        <select value={filterStatus} onChange={onFilterStatusChange} aria-label="Estado">
          <option>Todos los Estados</option>
          <option>Nueva</option>
          <option>En seguimiento</option>
          <option>Resuelta</option>
        </select>
      </span>

      <span className="ui-search">
        <i className="ri-search-line" />
        <input
          type="text"
          placeholder="Buscar alerta o distribuidor..."
          value={searchTerm}
          onChange={onSearchChange}
          onKeyDown={onSearchEnter}
        />
      </span>
    </>
  );

  // DataTable trabaja con pageIndex en base 0; la pantalla con página en base 1.
  const tablePagination = { pageIndex: currentPage - 1, pageSize };

  const setTablePagination = (next) => {
    const value = typeof next === 'function' ? next(tablePagination) : next;
    if (value.pageSize !== pageSize) setPageSize(value.pageSize);
    setCurrentPage(value.pageIndex + 1);
  };

  return (
    <DataTable
      data={alerts}
      columns={columns}
      total={pagination.total ?? 0}
      pagination={tablePagination}
      setPagination={setTablePagination}
      progressPending={isLoading}
      minWidth={MIN_WIDTH}
      filters={filters}
      emptyMessage="No hay alertas que coincidan con los filtros."
    />
  );
};

export default AlertTable;
